import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Wallet, Settings, Trophy, MessageSquare, LogOut, Menu, MessageCircle, Info, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getUserProgress } from "@/lib/gamification";
import { getStreakStatus } from "@/lib/gamification";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGamification } from "@/contexts/GamificationContext";
import { FEATURE_FLAGS } from "@/lib/config";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface UserProgress {
  xp: number;
  level: number;
  current_streak: number;
}

export const AppDrawer = () => {
  const { t } = useLanguage();
  const { settings } = useGamification();
  const calmMode = settings.hideRewards;
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [streakStatus, setStreakStatus] = useState<'active' | 'paused' | 'new'>('new');
  const [userName, setUserName] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser) {
      setUser(currentUser);
      
      // Load saved user name from localStorage
      const savedName = localStorage.getItem('userName');
      setUserName(savedName);
      
      const userProgress = await getUserProgress(currentUser.id);
      if (userProgress) {
        setProgress(userProgress);
        const status = getStreakStatus(userProgress);
        setStreakStatus(status);
      }
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
    setOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const levelName = progress ? ['Beginner', 'Mindful', 'Savvy', 'Expert', 'Master'][Math.min(progress.level - 1, 4)] : 'Beginner';

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="min-h-[44px] min-w-[44px]"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] sm:w-[320px] flex flex-col overflow-hidden p-0">
        {/* Fixed Header - Stays at top */}
        <div className="flex-shrink-0 p-6 pb-0">
          <SheetHeader className="text-left">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          </SheetHeader>

          {/* User Profile Section - Clickable */}
          {user && (
            <div 
              className="py-4 cursor-pointer hover:bg-muted/50 transition-colors rounded-lg"
              onClick={() => handleNavigation('/profile')}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-lg font-bold text-primary">
                    {(userName?.[0] || user.email?.[0])?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-base truncate max-w-[180px]">
                    {userName || user.email?.split('@')[0] || 'User'}
                  </p>
                  {/* Hide level display in calm mode */}
                  {!calmMode && (
                    <p className="text-xs text-muted-foreground">
                      Level {progress?.level || 1} - {levelName}
                    </p>
                  )}
                  {FEATURE_FLAGS.showBetaTesterBadge && (
                    <p className="text-xs text-primary/70 mt-0.5">
                      🧪 Beta Tester
                    </p>
                  )}
                </div>
              </div>
              
              {/* Hide streak/XP chips in calm mode */}
              {progress && !calmMode && (
                <div className="flex gap-2 text-xs">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10">
                    <span>🔥</span>
                    <span className="font-medium">{progress.current_streak}-day streak</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10">
                    <span>⚡</span>
                    <span className="font-medium">{progress.xp} XP</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto mobile-scroll-fix px-6 pb-6 pt-4">
          <Separator className="mb-4" />

        {/* ACTIONS Section */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-3 mb-2 uppercase tracking-wide">
            {t('nav.actions')}
          </p>
          <Button
            variant={isActive('/') ? 'secondary' : 'ghost'}
            className={cn(
              "w-full justify-start h-12",
              isActive('/') && "bg-secondary font-medium"
            )}
            onClick={() => handleNavigation('/')}
          >
            <MessageCircle className="mr-3 h-5 w-5" />
            {t('nav.chatHome')}
          </Button>
          <Button
            variant={isActive('/budget') ? 'secondary' : 'ghost'}
            className={cn(
              "w-full justify-start h-12",
              isActive('/budget') && "bg-secondary font-medium"
            )}
            onClick={() => handleNavigation('/budget')}
          >
            <Wallet className="mr-3 h-5 w-5" />
            {t('nav.budgetIcon')}
          </Button>
        </div>

        {/* PROGRESS Section - Hidden in calm mode */}
        {!calmMode && (
          <>
            <Separator className="my-4" />
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground px-3 mb-2 uppercase tracking-wide">
                {t('nav.progress')}
              </p>
              <Button
                variant={isActive('/badges') ? 'secondary' : 'ghost'}
                className={cn(
                  "w-full justify-start h-12",
                  isActive('/badges') && "bg-secondary font-medium"
                )}
                onClick={() => handleNavigation('/badges')}
              >
                <Trophy className="mr-3 h-5 w-5" />
                {t('nav.badgesIcon')}
              </Button>
            </div>
          </>
        )}

        <Separator className="my-4" />

        {/* SYSTEM Section */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-3 mb-2 uppercase tracking-wide">
            {t('nav.system')}
          </p>
          <Button
            variant={isActive('/settings') ? 'secondary' : 'ghost'}
            className={cn(
              "w-full justify-start h-12",
              isActive('/settings') && "bg-secondary font-medium"
            )}
            onClick={() => handleNavigation('/settings')}
          >
            <Settings className="mr-3 h-5 w-5" />
            {t('nav.settingsProfile')}
          </Button>
          
          {/* Hide Privacy & About in calm mode - accessible via Settings page */}
          {!calmMode && (
            <>
              <Button
                variant={isActive('/settings/privacy-analytics') ? 'secondary' : 'ghost'}
                className={cn(
                  "w-full justify-start h-12",
                  isActive('/settings/privacy-analytics') && "bg-secondary font-medium"
                )}
                onClick={() => handleNavigation('/settings/privacy-analytics')}
              >
                <Shield className="mr-3 h-5 w-5" />
                {t('nav.privacyAnalytics')}
              </Button>
              <Button
                variant={isActive('/about') ? 'secondary' : 'ghost'}
                className={cn(
                  "w-full justify-start h-12",
                  isActive('/about') && "bg-secondary font-medium"
                )}
                onClick={() => handleNavigation('/about')}
              >
                <Info className="mr-3 h-5 w-5" />
                {t('nav.aboutLegal')}
              </Button>
            </>
          )}
          
          <Separator className="my-4" />
          
          {/* Sign Out */}
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleSignOut}
          >
            <LogOut className="mr-3 h-5 w-5" />
            {t('nav.signOut')}
          </Button>
        </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
